import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { aufSitzungsende, kontextFrischHalten, kontextWiederherstellen } from "../daten/klient";
import { merklisteAbmelden, merklisteAngleichen } from "../daten/merkliste";
import * as R from "../daten/routen";
import { startausgangBeiFehler } from "../daten/startausgang";
import type { Ich, Recht, Rolle } from "../daten/typen";
import { buendelAus, rechteMerken, rollenMerken, wegErlaubt } from "./rechte";

/* ══════════════════════════════════════════════════════════════════
   Wer angemeldet ist — und was daraus folgt.

   Die Rolle entscheidet, welche Maske erreichbar ist, und zwar **nicht
   durch Ausgrauen, sondern durch Weglassen**: Wer kein Recht hat, sieht
   den Punkt gar nicht (28-designkonzept.md, „Die fünf Zustände").

   Das Kontext-Token liegt im Speicher von `daten/klient.ts`, nicht hier
   und nirgends im `localStorage`. Was hier liegt, ist nur, wer der
   Mensch ist.
   ══════════════════════════════════════════════════════════════════ */

type SitzungsWert = {
  ich: Ich | null;
  angemeldet: boolean;
  /** Solange der Wiedereinstieg läuft, steht weder Maske noch Anmeldung —
   *  sonst blitzt die Anmeldung auf und verschwindet wieder. */
  prueft: boolean;
  anmelden: (ich: Ich) => void;
  abmelden: () => Promise<void>;
  darf: (rollen: Rolle[]) => boolean;
  /** Darf er auf diesen Weg — aus seinen **Rechten**, nicht aus den Rollen (A-17). */
  darfAuf: (weg: string) => boolean;
  /** Hat er eines dieser Rechte — dieselbe Liste, die die API prüft. */
  darfRecht: (...rechte: Recht[]) => boolean;
  /**
   * **Darf diese Sitzung überhaupt schreiben?** (P6-01, 05.10.2026) — nein
   * für den Prüfer und für einen Betrieb im Nur-Lese-Zustand (`Ich.nurLesen`,
   * 0274). Der Riegel ist die API (`mitte/nurlesen.ts`); das hier sagt der
   * Maske, dass ein Knopf, der schreibt, gar nicht erst dasteht.
   */
  darfSchreiben: boolean;
};

const Raum = createContext<SitzungsWert | null>(null);

export function SitzungsRaum({ children }: { children: ReactNode }) {
  const [ich, setzeIch] = useState<Ich | null>(null);
  const [prueft, setzePrueft] = useState(true);

  // Läuft die Sitzung serverseitig aus, fällt die Oberfläche auf die
  // Anmeldung zurück — statt in Masken zu stehen, die nichts mehr laden.
  useEffect(() => aufSitzungsende(() => setzeIch(null)), []);
  useEffect(() => (ich ? kontextFrischHalten() : undefined), [ich]);

  /* **Die Sammlung gehört dem Menschen, nicht dem Browser** (Migration 0134).
     Sobald feststeht, wer angemeldet ist, treffen der Stand auf diesem Gerät
     und der auf dem Server aufeinander; der neuere gilt. Vorher geht das
     nicht: Ohne Token gibt es keine Sammlung, die jemandem gehört.

     Beim Abmelden wird sie von **diesem Gerät** genommen und drüben stehen
     gelassen. Am selben Rechner meldet sich der Nächste an, und der hat mit
     dem Anruf von vorhin nichts zu tun. */
  /* Einmal je Mensch, nicht je Antwort von `GET /ich` (T-45, 03.10.2026):
     Jede Erneuerung lieferte ein neues `ich`-Objekt, und die Merkliste wurde
     zwei- bis dreimal je Maske geholt. */
  const ichId = ich?.id ?? null;
  useEffect(() => {
    if (!ichId) return;
    void merklisteAngleichen();
  }, [ichId]);

  /* **Nach der Anmeldung wird neu gefragt** (Prüfrunde Runde 10, N10-03).
     Doris sah direkt nach der Anmeldung „Nichts, was bei dir liegt" und
     „Alles verteilt" — eine Antwort aus dem Speicher der Seite, gefragt, bevor
     sie es war. Wechselt der Mensch, gilt nichts mehr, was vorher geholt
     wurde: Die Abfragen fangen von vorn an (und zeigen, dass sie laden),
     beim Abmelden wird der Speicher geleert. */
  const abfragen = useQueryClient();
  const zuletzt = useRef<string | null>(null);
  useEffect(() => {
    if (!ichId) { zuletzt.current = null; return; }
    // Dieselbe Person mit erneuertem `GET /ich` (T-45) ist keine Anmeldung.
    if (zuletzt.current === ichId) return;
    zuletzt.current = ichId;
    if (abfragen.getQueryCache().getAll().length) void abfragen.resetQueries();
  }, [ichId, abfragen]);

  /* **Die Rolle wandert neben den React-Baum** (`schale/rechte.ts`). Der
     Wegwächter sitzt im Routenbaum und läuft, bevor eine Maske lädt; dort
     gibt es keinen Kontext, aus dem er sie lesen könnte.

     `null` heißt „noch unbekannt" und ist nicht dasselbe wie „keine Rolle":
     Solange der Wiedereinstieg aus dem Cookie läuft, darf niemand
     weggeschoben werden. **Und wer gar nicht angemeldet ist, ist auch
     unbekannt** — sonst hielte der Wegwächter die Anmeldung für einen
     Menschen ohne Rolle und schöbe sie auf den Telefonschirm; wer sich
     danach anmeldet, stünde auf `/handy` statt dort, wo er hinwollte. */
  useEffect(() => {
    rollenMerken(prueft || !ich ? null : ich.rollen);
    /* **Und die Rechte daneben** (Migration 0147, 18.09.2026). Was ein
       einzelner Mensch dazubekommen oder verloren hat, steht in keiner Rolle
       — ohne diese Zeile zeigte die Leiste ihm einen Weg, den die API ihm
       verweigert. `undefined` (ältere API) heißt „rechne aus den Rollen". */
    rechteMerken(prueft || !ich ? null : (ich.rechte ?? null));
  }, [ich, prueft]);

  // Einmal beim Start: Läuft die Sitzung serverseitig noch, wird sie aus
  // dem Cookie geholt. Das Token selbst bleibt im Speicher.
  //
  // **Ohne Netz beim Start wird es nachgeholt, sobald das Netz zurück ist**
  // (T-41, 03.10.2026). Bis dahin stand die Anmeldung da und blieb stehen,
  // auch als der Empfang wieder da war — erst ein Neuladen fand die Sitzung,
  // die die ganze Zeit gültig war.
  useEffect(() => {
    let gilt = true;
    const holen = async (): Promise<"da" | "aus" | "netz"> => {
      try {
        const ausgang = await kontextWiederherstellen();
        if (ausgang === "da") {
          const wer = await R.ich();
          if (gilt) setzeIch(wer);
        }
        return ausgang;
      } catch (e) {
        /* Keine Sitzung — dann gehört die Anmeldung hin. **Außer, das Netz
           fehlte** (A-10-Nebenbefund): Dann kam der Kontext noch durch und
           `GET /ich` nicht, und die Sitzung gilt weiter. */
        return startausgangBeiFehler(e);
      }
    };
    let beiNetz: (() => void) | null = null;
    /* Wartet auf das Netz und holt nach — so oft, wie es beim Nachholen
       wieder am Netz scheitert. */
    const aufNetzWarten = () => {
      beiNetz = () => {
        if (beiNetz) window.removeEventListener("online", beiNetz);
        beiNetz = null;
        void holen().then((neu) => { if (neu === "netz" && gilt) aufNetzWarten(); });
      };
      window.addEventListener("online", beiNetz);
    };
    void (async () => {
      const ausgang = await holen();
      if (gilt) setzePrueft(false);
      if (ausgang === "netz" && gilt) aufNetzWarten();
    })();
    return () => {
      gilt = false;
      if (beiNetz) window.removeEventListener("online", beiNetz);
    };
  }, []);

  const abmelden = useCallback(async () => {
    await R.abmelden();
    merklisteAbmelden();
    setzeIch(null);
    // Was der Vorige gesehen hat, sieht der Nächste nicht (N10-03).
    abfragen.clear();
  }, [abfragen]);

  const wert = useMemo<SitzungsWert>(() => {
    /* **Die Rechte, wie der Server sie gerechnet hat** (`GET /ich`, 0147) —
       Bündel der Rollen, dazu die eigenen, ohne die entzogenen. Nur eine
       ältere API schickt sie nicht; dann die Bündel aus dem Code. */
    const rechte: Recht[] = ich ? (ich.rechte ?? buendelAus(ich.rollen)) : [];
    return {
      ich,
      angemeldet: ich !== null,
      prueft,
      anmelden: setzeIch,
      abmelden,
      darf: (rollen) => {
        if (!rollen.length) return true;
        if (!ich) return false;
        return rollen.some((r) => ich.rollen.includes(r));
      },
      darfAuf: (weg) => wegErlaubt(rechte, weg),
      darfRecht: (...verlangt) => verlangt.some((r) => rechte.includes(r)),
      darfSchreiben: darfSchreibenFuer(ich),
    };
  }, [ich, prueft, abmelden]);

  return <Raum.Provider value={wert}>{children}</Raum.Provider>;
}

export function useSitzung(): SitzungsWert {
  const wert = useContext(Raum);
  if (!wert) throw new Error("useSitzung braucht einen SitzungsRaum darüber.");
  return wert;
}

/**
 * **Der eine Baustein für „darf hier geschrieben werden"** (P6-01).
 *
 * Die Prüferin sah am Auftrag „Übergeben", „Per Mail senden", „Im Brett
 * zuteilen" und die Auswahl des Zahlungsziels; die Wahl sprang still zurück,
 * weil die API mit 403 `recht.nur_lesen` abwies. Jede Maske, die einen
 * schreibenden Knopf zeigt, fragt hier — nicht nach der Rolle: Ein Betrieb
 * auf Lesen (Vertragsende) liest genauso nur.
 */
export function useDarfSchreiben(): boolean {
  return useSitzung().darfSchreiben;
}

/** Rein — `test/nurlesen-oberflaeche.test.ts`. */
export function darfSchreibenFuer(ich: Pick<Ich, "nurLesen"> | null): boolean {
  return !ich?.nurLesen;
}
